import React, { useEffect, useRef, useState } from 'react'

import { CPF } from 'gerador-validador-cpf'

import { Typography, TextInput, Paper, Select } from '@telavita-core/react-design-kit'
import ModalGeneric from 'containers/ModalManager/Modals/ModalGeneric'
import EnterpriseSuccessModal from './components/EnterpriseSuccessModal'
import CorporateSuccessModal from './components/CorporateSuccessModal'

import * as S from './styles'

import { INACTIVE } from 'settings/_personSettings'
import { planGroupsSettings, PlanGroupCategoryEnum } from 'settings/_planGroupSettings'

import PatientsService from 'services/patients'
import { getCorporateEligibility } from 'services/corporateEligibilityService'
import { isCardNumberAvailiable } from 'services/patients/isCardNumberAvailiable'
import { updatePatientInfo } from 'services/persons/updatePatientInfo'

interface GroupPlanSettings {
  identifier: {
    mask: string
    placeholder: string
  }
  useCpfInCardNumber?: boolean | undefined
}

interface PlanGroupData {
  code: string,
  name: string,
  category: PlanGroupCategoryEnum,
  logo_url?: string
  plans?: Array<{
    code: string
    id: number
    is_particular: boolean
    name: string
  }>
}

interface onSubmitData {
  patientName?: string
  planCode: string
  userName: string
  cardNumber?: string
}

interface LinkPlanModalProps {
  planGroups: PlanGroupData[]
  patientCpf: string
  userName: string
  patientPlans: Array<{
    partner_plan_code: string
    partner_plan_name: string
    status_code: string
    partner_plan_is_particular: boolean
    plan_group: {
      code: string
      name: string
    }
  }>
  onClose: () => void
  onLinkPlan: ({ patientName, planCode, userName, cardNumber }: onSubmitData) => Promise<void>
}

export interface SelectOption {
  id: string;
  label: string;
  handler?: (option?: SelectOption) => void;
}

const LinkPlanModal = ({
  planGroups,
  patientCpf,
  patientPlans,
  userName,
  onClose,
  onLinkPlan
}: LinkPlanModalProps): JSX.Element => {

  const patientPlanWithoutParticular = patientPlans?.filter(pPlan => !pPlan.partner_plan_is_particular)
  
  const isPatientFirstPlan = useRef(patientPlanWithoutParticular.length === 0)

  const [selectedPlanGroup, setSelectedPlanGroup] = useState<SelectOption | null>(null)
  const [showWarningMessage, setShowWarningMessage] = useState(false)

  const [cpf, setCpf] = useState<string | null>(null)
  const [name, setName] = useState<string | null>(null)
  const [plan, setPlan] = useState<SelectOption | null>(null)
  const [cardNumber, setCardNumber] = useState<string | null>(null)

  const [cpfMsg, setCpfMsg] = useState('')
  const [planMsg, setPlanMsg] = useState('')
  const [cardNumberMsg, setCardNumberMsg] = useState('')

  const [isCpfValid, setIsCpfValid] = useState<boolean>(false)
  const [isCardNumberValid, setIsCardNumberValid] = useState<boolean>(false)

  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const groupedPlansDropdownOptions = planGroups
    ? planGroups.map((planGroup) => ({
      id: planGroup.code,
      label: planGroup.name,
    }))
    : []

  const { ENTERPRISE, CARE_PLUS, CORPORATE_LIMITED, CORPORATE_UNLIMITED, CAPITATION } = PlanGroupCategoryEnum

  const currentPlanGroup = !!selectedPlanGroup && planGroups && planGroups.find(plan => plan.code === selectedPlanGroup.id)

  const plansDropdownOptions = currentPlanGroup
    ? currentPlanGroup.plans.map((plan) => ({
      id: plan.code,
      label: plan.name,
    })).sort((a, b) => (a.label > b.label) ? 1 : -1)
    : []
    
  const hasPlanGroupSelected = !!selectedPlanGroup && currentPlanGroup

  const planGroupCategory = hasPlanGroupSelected && currentPlanGroup.category

  const settings = !!selectedPlanGroup && planGroupsSettings[selectedPlanGroup.id] as GroupPlanSettings

  const isCorporate = ([CORPORATE_LIMITED, CORPORATE_UNLIMITED, CAPITATION]).includes(planGroupCategory)
  const isEnterprise = ([CARE_PLUS, ENTERPRISE]).includes(planGroupCategory)
  const isCapitation = planGroupCategory === CAPITATION

  const isReadyToSubmit =
    (isEnterprise && name && plan && !planMsg && isCardNumberValid && !cardNumberMsg) ||
    ((isCorporate || settings.useCpfInCardNumber) && (isCpfValid || !!patientCpf) && plan && !planMsg)

  const showCpfInput = isCorporate || settings.useCpfInCardNumber

  const cleanFormData = () => {
    setName(null)
    setCpf(null)
    setPlan(null)
    setCardNumber(null)

    setCpfMsg('')
    setCardNumberMsg('')
    setPlanMsg('')
    setIsCpfValid(false)
    setIsCardNumberValid(false)
  }

  const handleCardNumber = (e: React.ChangeEvent<HTMLInputElement>, mask: string) => {
    const inputValue = e.target.value
    const inputCleanedValue = inputValue.replace(/_+/g, '').replace(/\.+/g, '')
    const minLength = mask && mask.match(/1/g)?.length

    setCardNumber(inputCleanedValue)
    setIsCardNumberValid(mask ? inputCleanedValue.length === minLength : inputCleanedValue.length >= 1)
    setCardNumberMsg('')
  }

  const handleSelectGroupPlan = (selectedPlanGroup: SelectOption) => {
    cleanFormData()
    setSelectedPlanGroup(selectedPlanGroup)

    const patientAlreadyHasGroupPlan = patientPlans.some(patientPlan => patientPlan.plan_group.code === selectedPlanGroup.id)

    setShowWarningMessage(patientAlreadyHasGroupPlan)
  }

  const handleSelectPlan = (selectedPlan: SelectOption) => {

    setPlan(selectedPlan)

    const patientAlreadyHasPlan = patientPlans.some(patientPlan => patientPlan.partner_plan_code === selectedPlan.id && patientPlan.status_code !== INACTIVE)

    setPlanMsg(patientAlreadyHasPlan ? '* O paciente já está vinculado a este plano.' : '')
  }

  const checkIfCardNumberExists = async (): Promise<boolean> => {
    try {
      await isCardNumberAvailiable(cardNumber, selectedPlanGroup.id)

      return true
    } catch(err: unknown) {
      if (err === 404) {
        return false
      }
    }
  }

  const onSubmit = async () => {
    try {
      setIsSubmitting(true)

      const currentPatientCpf = patientCpf ?? cpf 

      if (isCorporate) {

        if (!patientCpf) {
          await updatePatientInfo({
            cpf: cpf.replace(/\.|-/g, '')
          })
        }

        const { isEligible } = await getCorporateEligibility({
          username: userName,
          planCode: plan.id
        })

        if (!isEligible) {
          setCpfMsg('Não conseguimos validar o número do CPF. Verifique se ele está correto ou entre em contato com o convênio.')
          setIsSubmitting(false)
          return
        }
      } else if (isEnterprise) {
        const cardNumberExists = await checkIfCardNumberExists()

        if (cardNumberExists) {
          setCardNumberMsg('Carteirinha já cadastrada')
          setIsSubmitting(false)
          return
        }
      }

      await onLinkPlan({
        patientName: name,
        userName: userName,
        ...(cardNumber && { cardNumber: cardNumber }),
        ...(isCapitation && { cardNumber: currentPatientCpf }),
        planCode: plan.id,
      })
      setShowSuccessMessage(true)
      setIsSubmitting(false)
      setCpfMsg('')
      setCardNumberMsg('')

    } catch (error: unknown) {
      setShowSuccessMessage(false)
      setIsSubmitting(false)
    }
  }

  function handleOpenChat() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    window.octadesk.chat.toggle()
    onClose()
  }

  useEffect(() => {
    if (cpf === null) {
      setIsCpfValid(false)
      setCpfMsg('')
      return
    }

    const isValid = CPF.validate(cpf) //eslint-disable-line
    const formattedCpf = CPF.format(cpf, 'digits') //eslint-disable-line

    if (cpf) {
      if (isValid) {
        PatientsService.isCpfAvailable(formattedCpf)
          .then(() => {
            setIsCpfValid(false)
            setCpfMsg('CPF já cadastrado')
          })
          .catch(() => {
            setIsCpfValid(true)
            setCpfMsg('')
          })

      } else {
        setIsCpfValid(false)
        setCpfMsg('CPF inválido')
      }
    } else {
      setIsCpfValid(false)
      setCpfMsg('O Campo é obrigatório')
    }
  }, [cpf])
    
  if (showSuccessMessage && (isEnterprise || isCapitation)) return <EnterpriseSuccessModal onClose={onClose} isPatientFirstPlan={isPatientFirstPlan.current}/>

  if (showSuccessMessage && isCorporate) return <CorporateSuccessModal onClose={onClose} isPatientFirstPlan={isPatientFirstPlan.current}/>

  return (
    <ModalGeneric
      modalTitle={'Adicionar convênio'}
      customClassName={'LinkPlanModal'}
      btnText='PROSSEGUIR'
      btnOnClick={onSubmit}
      hasButton={!!selectedPlanGroup && !showWarningMessage}
      onCloseModal={onClose}
      btnDisabled={!isReadyToSubmit || isSubmitting}
      btnLoading={isSubmitting}
    >
      <S.Container>
        <Select 
          options={groupedPlansDropdownOptions}
          placeholder='Selecione um convênio'
          onSelect={handleSelectGroupPlan}
          position='fixed'
          selected={selectedPlanGroup}
          label={null}
          customClassName='LinkPlanModal__Select'
        />
        {showWarningMessage && (
          <Paper variant='warning' customClassName='LinkPlanModal__WarningMessageWrapper'>
            <Typography variant='content2' weight='bolder'>
              VOCÊ JÁ POSSUI VINCULAÇÃO COM ESTE CONVÊNIO
            </Typography>
            <Typography variant='content2'>
              Caso necessite mudar de plano ou <br /> 
              adicionar um novo dentro deste mesmo <br /> 
              convênio, <b onClick={handleOpenChat}>clique aqui</b> e fale com nossa <br /> equipe pelo chat.
            </Typography>
          </Paper>
        )}
        {!!selectedPlanGroup && !showWarningMessage && (
          <>
            <S.PlanImage
              src={currentPlanGroup?.logo_url}
            />

            <Typography variant='content2' customClassName='LinkPlanModal__FormTitle'>
              Insira os dados do convênio:
            </Typography>
            <S.FormWrapper>
              {isEnterprise && (
                <TextInput
                  name='name'
                  onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                  value={name}
                  placeholder='Nome completo (como na carteirinha) *'        
                />
              )}
              <Select 
                options={plansDropdownOptions}
                placeholder='Selecione um plano'
                onSelect={handleSelectPlan}
                position='fixed'
                selected={plan}
                label={null}
                customClassName='LinkPlanModal__Select'
              />
              {showCpfInput && (
                <TextInput
                  hasError={!!cpfMsg}
                  messageError={cpfMsg}
                  name="cpf"
                  onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => setCpf(e.target.value)}
                  placeholder="CPF"
                  required
                  type="text"
                  disabled={!!patientCpf}
                  value={patientCpf ? patientCpf : cpf}
                  mask='111.111.111-**'
                />
              )}
              {isEnterprise && !showCpfInput && (
                <TextInput
                  name='card_number'
                  messageError={cardNumberMsg}
                  hasError={!!cardNumberMsg}
                  placeholder={settings?.identifier.placeholder}
                  mask={settings?.identifier.mask ? settings?.identifier.mask : ''}
                  value={cardNumber}
                  onInputChange={e => handleCardNumber(e, settings?.identifier.mask)}
                />
              )}
            </S.FormWrapper>
            <Typography customClassName='LinkPlanModal__Label' variant='content2' italic>
              *Preenchimento obrigatório
            </Typography>
          </>
        )}
      </S.Container>
    </ModalGeneric>
  )
}

export default LinkPlanModal